<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Patient journey details"
      :subtitle="headerSubtitle"
      :show-dates="false"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @refresh="refreshLive"
    >
      <template #actions>
        <router-link class="btn trk-btn-ghost" :to="backLink">{{ backLabel }}</router-link>
        <span v-if="session" class="trk-pill" :class="statusTone(session.status)" :title="journeyStatusTitle(session)">{{ prettyJourneyStatus(session) }}</span>
        <span v-if="failureCount" class="trk-pill bad">{{ failureCount }} HTTP 500</span>
      </template>
    </tracking-page-header>

    <div class="trk-panel mb-3" v-if="loading && !session">
      <div class="trk-panel-body">
        <div class="trk-empty"><strong>Loading journey…</strong>Fetching timeline for this session.</div>
      </div>
    </div>

    <div class="trk-panel mb-3" v-else-if="loadError">
      <div class="trk-panel-body">
        <div class="trk-empty">
          <strong>Could not load this journey</strong>
          {{ loadError }}
        </div>
      </div>
    </div>

    <div class="trk-panel mb-3" v-if="session">
      <div class="trk-panel-head">
        <div>
          <h2>Journey summary</h2>
          <p>Key facts about this patient's booking attempt</p>
        </div>
        <button type="button" class="btn trk-btn-ghost btn-sm" @click="downloadJourney">
          Download journey
        </button>
        <button type="button" class="btn trk-btn-ghost btn-sm" @click="copySessionId" :title="sessionId">
          Copy session ID
        </button>
      </div>
      <div class="trk-panel-body">
        <b-row>
          <b-col md="3">
            <div class="meta-item"><label title="Linked patient account if they registered or logged in">Patient</label><div>
              <router-link v-if="session.patientId" :to="'/patient/' + session.patientId">#{{ session.patientId }}</router-link>
              <span v-else-if="leadName">Not linked yet ({{ leadName }})</span>
              <span v-else>Not linked yet</span>
            </div></div>
            <div class="meta-item" v-if="session.leadEmail"><label>Email</label><div>{{ session.leadEmail }}</div></div>
            <div class="meta-item" v-if="session.leadPhone"><label>Phone</label><div>{{ session.leadPhone }}</div></div>
            <div class="meta-item"><label title="Furthest booking step reached so far">Current step</label><div>{{ prettyStep(session.currentStep) }}</div></div>
            <div class="meta-item"><label title="New patient vs already signed in">Patient type</label><div>{{ prettyUserType(session.userType) }}</div></div>
            <div class="meta-item" v-if="session.orderId || session.appointmentId"><label title="Appointment was booked even if they left later">Booking</label><div><span class="trk-pill ok">Appointment booked</span></div></div>
            <div class="meta-item"><label title="Direct = typed URL. Booking website = clicked from book.thegpservice.co.uk">How they arrived</label><div :title="sourceHint(session.sourceKey, session)">{{ formatSource(session) }}</div></div>
            <div class="meta-item"><label title="First surface that started this journey (widget, register, payment…)">Entry surface</label><div>{{ prettyEntrySurface(session.entrySurface) }}</div></div>
            <div class="meta-item"><label title="Exact page path when the journey started">Entry page</label><div class="text-truncate" :title="session.entryPage">{{ session.entryPage || '-' }}</div></div>
          </b-col>
          <b-col md="3">
            <div class="meta-item"><label>Started</label><div>{{ formatDateTime(session.startedAt) }}</div></div>
            <div class="meta-item"><label>Last activity</label><div>{{ relativeTime(session.lastActivityAt) }}</div></div>
            <div class="meta-item"><label>Time on journey</label><div>{{ formatDurationMs(session.durationMs) }}</div></div>
          </b-col>
          <b-col md="3">
            <div class="meta-item"><label>Device</label><div>{{ capitalize(session.deviceType) }}</div></div>
            <div class="meta-item"><label>Status</label><div><span class="trk-pill" :class="statusTone(session.status)" :title="journeyStatusTitle(session)">{{ prettyJourneyStatus(session) }}</span></div></div>
            <div class="meta-item"><label>Platform</label><div>{{ session.platform || '-' }}</div></div>
            <div class="meta-item"><label>Visitor</label><div class="text-truncate" :title="session.visitorId"><code>{{ session.visitorId || '-' }}</code></div></div>
            <div class="meta-item" v-if="session.previousSessionId"><label>Previous session</label><div class="text-truncate" :title="session.previousSessionId"><code>{{ session.previousSessionId }}</code></div></div>
          </b-col>
          <b-col md="3">
            <div class="meta-item"><label>Order</label><div>{{ session.orderId || '-' }}</div></div>
            <div class="meta-item"><label>Appointment</label><div>{{ session.appointmentId || '-' }}</div></div>
            <div class="meta-item"><label>Session</label><div class="text-truncate" :title="sessionId"><code>{{ sessionId }}</code></div></div>
          </b-col>
        </b-row>
      </div>
    </div>

    <gps-ai-feature
      v-if="session"
      class="mb-3"
      :feature="'journey/' + sessionId"
      title="Explain this journey"
      purpose="Reads this patient's whole timeline and says what happened, where and why they stopped, and what to do next."
      run-label="Explain with GPS AI"
    >
      <template v-slot="{ result }">
        <p class="font-weight-bold mb-2">{{ result.headline }}</p>
        <p class="mb-2">{{ result.whatHappened }}</p>
        <div class="mb-1"><strong>Where they stopped:</strong> {{ result.whereTheyStopped }}</div>
        <div class="mb-1"><strong>Why:</strong> {{ result.why }}</div>
        <div><strong>Do next:</strong> {{ result.nextAction }}</div>
      </template>
    </gps-ai-feature>

    <div class="trk-panel mb-3" v-if="session && dropOffHint">
      <div class="trk-panel-body">
        <div class="trk-lost-callout" :class="dropOffHint.tone">
          <strong>{{ dropOffHint.title }}</strong>
          <p class="mb-0">{{ dropOffHint.body }}</p>
        </div>
      </div>
    </div>

    <div class="trk-panel" v-if="session">
      <div class="trk-panel-head">
        <div>
          <h2>What the patient saw</h2>
          <p>Widget = booking widget only. Checkout onward = register page. Badge = times opened (refresh counts; hidden when only once).</p>
        </div>
        <label class="trk-live-auto" title="Automatically reload steps every 10 seconds while you watch">
          <input type="checkbox" v-model="liveAutoRefresh" @change="onLiveAutoRefreshChange" />
          <span>Auto-refresh</span>
        </label>
      </div>
      <div class="trk-panel-body">
        <div class="trk-progress-ribbon">
          <button
            type="button"
            v-for="step in progressSteps"
            :key="step.code"
            class="trk-progress-step"
            :class="[step.state, { selected: selectedUiStage === step.code }]"
            :title="step.hint"
            @click="selectUiStage(step.code)"
          >
            <span
              v-if="step.visitCount > 1"
              class="trk-visit-badge"
              :title="'Opened ' + step.visitCount + ' times (includes page refreshes)'"
            >{{ step.visitCount }}</span>
            <div class="trk-progress-dot">
              <i v-if="step.state === 'done'" class="fa fa-check"></i>
              <i v-else-if="step.state === 'skipped'" class="fa fa-minus"></i>
              <i v-else-if="step.state === 'current'" class="fa fa-circle"></i>
              <span v-else>{{ step.index }}</span>
            </div>
            <div class="trk-progress-label">{{ step.short }}</div>
          </button>
        </div>
        <div v-if="selectedUiStage === 'CHECKOUT'" class="trk-checkout-micro mt-3">
          <label class="trk-kpi-label">Inside Checkout</label>
          <div class="trk-micro-row">
            <button
              type="button"
              class="trk-micro-chip"
              :class="{ active: selectedUiStage === 'CHECKOUT' && !filters.step }"
              @click="clearCheckoutMicro"
            >All of Checkout</button>
            <button
              type="button"
              v-for="micro in checkoutMicroSteps"
              :key="micro.code"
              class="trk-micro-chip"
              :class="{ active: filters.step === micro.code }"
              :title="'Checkout — ' + micro.label"
              @click="filterCheckoutMicro(micro.code)"
            >{{ micro.short }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="trk-panel" v-if="session">
      <div class="trk-panel-head">
        <div>
          <h2>Step-by-step timeline</h2>
          <p>{{ newestFirst ? 'Newest activity first — use Clicks only for patient taps' : 'Oldest activity first — use Clicks only for patient taps' }}</p>
        </div>
        <div class="trk-timeline-actions">
          <span class="trk-pill info">{{ filteredEvents.length }} activities</span>
          <button type="button" class="btn trk-btn-ghost btn-sm" @click="toggleOrder">
            {{ newestFirst ? 'Show oldest first' : 'Show newest first' }}
          </button>
        </div>
      </div>
      <div class="trk-panel-body" :class="{ 'trk-panel-body--loading': loading }">

      <div v-if="loading" class="trk-inline-loading mb-3">
        <i class="fa fa-circle-o-notch fa-spin"></i>
        Loading events for this journey…
      </div>

      <b-row class="mb-3 tracking-filters align-items-end">
        <b-col md="3" class="mb-2">
          <label class="trk-kpi-label" title="Show only activities of this type">Activity type</label>
          <select class="form-control" v-model="filters.kind" @change="applyFilters" title="Filter: patient clicks vs backend APIs vs journey milestones">
            <option value="">All activities</option>
            <option value="ui">Patient actions (clicks / fields)</option>
            <option value="api">Backend API calls</option>
            <option value="journey">Journey milestones</option>
          </select>
        </b-col>
        <b-col md="3" class="mb-2">
          <label class="trk-kpi-label" title="Show only events from this booking step">Step</label>
          <select class="form-control" v-model="filters.step" @change="applyFilters" title="Filter timeline by booking step">
            <option value="">All steps</option>
            <option v-for="opt in stepOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </b-col>
        <b-col md="3" class="mb-2">
          <label class="trk-kpi-label" title="Show only successes, failures, or info events">Result</label>
          <select class="form-control" v-model="filters.outcome" @change="applyFilters" title="Filter by whether the action worked or failed">
            <option value="">All results</option>
            <option value="SUCCESS">Worked</option>
            <option value="FAILURE">HTTP 500</option>
            <option value="INFO">Info</option>
          </select>
        </b-col>
        <b-col md="3" class="mb-2">
          <label class="trk-kpi-label d-block">&nbsp;</label>
          <button
            type="button"
            class="btn btn-sm mr-1"
            :class="filters.kind === 'ui' ? 'trk-btn-primary' : 'trk-btn-ghost'"
            title="Show only form field clicks and button presses"
            @click="showPatientClicksOnly"
          >
            Clicks only{{ clickSummary.length ? ` (${clickSummary.length})` : '' }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="filters.outcome === 'FAILURE' ? 'trk-btn-primary' : 'trk-btn-ghost'"
            title="Show only HTTP 500 API errors"
            @click="showFailuresOnly"
          >
            500s{{ failureCount ? ' (' + failureCount + ')' : '' }}
          </button>
          <button
            v-if="filters.step || filters.outcome || filters.kind"
            type="button"
            class="btn trk-btn-ghost btn-sm ml-1"
            title="Clear all timeline filters"
            @click="clearFilters"
          >
            Clear
          </button>
        </b-col>
      </b-row>

      <div v-if="!loading && !filteredEvents.length" class="trk-empty mb-3">
        <strong>{{ emptyFilterTitle }}</strong>
        {{ emptyFilterHint }}
        <div class="mt-2" v-if="selectedUiStage && selectedUiStage !== 'CHECKOUT'">
          <button type="button" class="btn trk-btn-primary btn-sm" @click="selectUiStage('CHECKOUT')">Show Checkout activity</button>
          <button type="button" class="btn trk-btn-ghost btn-sm ml-1" @click="clearFilters">Clear filters</button>
        </div>
      </div>

      <div class="trk-table-refresh-bar" v-if="filteredEvents.length || !loading">
        <span class="trk-table-refresh-hint">Timeline</span>
        <button
          type="button"
          class="btn trk-btn-ghost btn-sm"
          title="Reload steps without scrolling to the top"
          :disabled="loading"
          @click="refreshLive"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>

      <b-table
        v-if="filteredEvents.length"
        :items="timelineDisplayRows"
        :fields="fields"
        hover
        responsive
        class="timeline-table"
        :tbody-tr-class="timelineRowClass"
        @row-clicked="onTimelineRowClicked"
      >
        <template v-slot:cell(time)="row">
          <span v-if="row.item._rowType === 'gap'" class="trk-wait-gap-time">pause</span>
          <template v-else>
            {{ formatTime(row.item.occurredAt || row.item.createdAt) }}
          </template>
        </template>
        <template v-slot:cell(kind)="row">
          <template v-if="row.item._rowType !== 'gap'">
            <span class="trk-pill" :class="kindTone(row.item)" :title="describeActivity(row.item.eventName, row.item).hint">
              {{ describeActivity(row.item.eventName, row.item).kindLabel }}
            </span>
          </template>
        </template>
        <template v-slot:cell(eventName)="row">
          <div v-if="row.item._rowType === 'gap'" class="trk-wait-gap">
            <span class="trk-wait-gap-line"></span>
            <span class="trk-wait-gap-chip">{{ waitGapLabel(row.item) }}</span>
            <span class="trk-wait-gap-line"></span>
          </div>
          <template v-else>
            <span :title="describeActivity(row.item.eventName, row.item).hint">
              {{ prettyEvent(row.item.eventName, row.item) }}
            </span>
            <div v-if="row.item._repeatHint" class="trk-field-repeat-hint">{{ row.item._repeatHint }}</div>
          </template>
        </template>
        <template v-slot:cell(step)="row">
          <template v-if="row.item._rowType !== 'gap'">
            {{ eventStepLabel(row.item) }}
          </template>
        </template>
        <template v-slot:cell(outcome)="row">
          <template v-if="row.item._rowType !== 'gap'">
            <span class="trk-pill" :class="statusTone(row.item)">{{ outcomeLabel(row.item) }}</span>
            <button
              v-if="isFailure(row.item)"
              type="button"
              class="btn btn-link btn-sm event-info-btn p-0 ml-1"
              title="View error details from the backend or form"
              @click.stop="openEventDetail(row.item)"
            >
              <i class="fa fa-exclamation-circle text-danger"></i>
            </button>
          </template>
        </template>
        <template v-slot:cell(timeSpent)="row">
          <template v-if="row.item._rowType !== 'gap'">
            <div class="time-spent">
              <div
                class="time-spent-primary"
                :class="{ 'time-spent-wait': Number(row.item.gapMs) > 1000 }"
              >{{ formatTimeSpent(row.item) }}</div>
              <div v-if="actionDurationMs(row.item) != null" class="time-spent-secondary">action {{ formatShortMs(actionDurationMs(row.item)) }}</div>
            </div>
          </template>
        </template>
        <template v-slot:cell(actions)="row">
          <template v-if="row.item._rowType !== 'gap'">
            <button type="button" class="btn btn-link btn-sm p-0" title="View full details for this activity" @click.stop="openEventDetail(row.item)">
              <i class="fa fa-info-circle"></i>
            </button>
          </template>
        </template>
      </b-table>
      </div>
    </div>

    <b-modal
      id="event-detail-modal"
      v-model="detailOpen"
      title="What happened"
      size="lg"
      ok-only
      ok-title="Close"
      ok-variant="success"
      body-class="event-detail-body"
    >
      <div v-if="selectedEvent">
        <b-row>
          <b-col md="6">
            <div class="meta-item"><label>Event</label><div>{{ prettyEvent(selectedEvent.eventName, selectedEvent) }}</div></div>
            <div class="meta-item"><label>Step</label><div>{{ prettyStep(selectedEvent.step) }}</div></div>
            <div class="meta-item"><label>Result</label><div>
              <span class="trk-pill" :class="statusTone(selectedEvent)">{{ outcomeLabel(selectedEvent) }}</span>
            </div></div>
          </b-col>
          <b-col md="6">
            <div class="meta-item"><label>Time</label><div>{{ formatDateTime(selectedEvent.occurredAt || selectedEvent.createdAt) }}</div></div>
            <div class="meta-item"><label>Page</label><div>{{ selectedEvent.page || '-' }}</div></div>
            <div class="meta-item"><label>Time spent</label><div>{{ formatTimeSpent(selectedEvent) }}</div></div>
          </b-col>
        </b-row>

        <div v-if="isFailure(selectedEvent) || hasErrorFields(selectedEvent)" class="event-error-box mt-3">
          <h6 class="mb-2">Error</h6>
          <div class="meta-item"><label>Error code</label><div>{{ selectedEvent.errorCode || metaValue(selectedEvent, 'failureCode') || '-' }}</div></div>
          <div class="meta-item"><label>Message</label><div>{{ errorMessageOf(selectedEvent) || '-' }}</div></div>
          <div class="meta-item"><label>HTTP status</label><div>{{ selectedEvent.httpStatus != null ? selectedEvent.httpStatus : '-' }}</div></div>
          <div class="meta-item"><label>API path</label><div>{{ selectedEvent.apiPath || '-' }}</div></div>
        </div>

        <div v-if="showFailedApiPayload(selectedEvent)" class="event-error-box mt-3">
          <h6 class="mb-2">Failed API request (curl)</h6>
          <pre class="api-payload-curl">{{ eventApiCurl(selectedEvent) }}</pre>
          <div v-if="responseBodyOf(selectedEvent)" class="mt-2">
            <div class="api-payload-label">Response</div>
            <pre class="api-payload-curl api-payload-curl-muted">{{ responseBodyOf(selectedEvent) }}</pre>
          </div>
        </div>

        <div class="mt-3">
          <h6 class="mb-2">Details / options</h6>
          <div v-if="metadataEntries(selectedEvent).length === 0" class="text-muted">No extra details recorded</div>
          <table v-else class="table table-sm event-meta-table mb-0">
            <tbody>
              <tr v-for="entry in metadataEntries(selectedEvent)" :key="entry.key">
                <th>{{ prettyMetaKey(entry.key) }}</th>
                <td>{{ entry.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import GpsAiFeature from './GpsAiFeature.vue';
import * as trackingLabels from './trackingShared';
import {
  STEP_FILTER_OPTIONS,
  describeActivity,
  isServerErrorStatus,
  isTrackedApiFault,
  isApiActivity,
  prettyUserType,
  toPatientUiStage,
  patientUiStepOrder,
  CHECKOUT_MICRO_STEPS,
  isWidgetEvent,
  isRegisterEvent,
  formatUkDateTime,
  formatUkTime,
  formatReferrer,
  sourceHint
} from './trackingShared';
import { STEPS } from './trackingWords';
import { buildEventApiCurl } from '../../utils/buildCurlCommand';

const PATIENT_API_BASE =
  process.env.VUE_APP_PATIENT_API || process.env.VUE_APP_GPS_API || 'http://localhost:8080';

export default {
  name: 'SessionDetail',
  components: { TrackingPageHeader, GpsAiFeature },
  props: {
    sessionId: { type: String, required: true }
  },
  data() {
    return {
      session: null,
      events: [],
      filteredEvents: [],
      loading: false,
      loadError: '',
      newestFirst: true,
      stepOptions: STEP_FILTER_OPTIONS,
      selectedUiStage: '',
      checkoutMicroCodes: CHECKOUT_MICRO_STEPS,
      filters: {
        step: '',
        outcome: '',
        kind: ''
      },
      fields: [
        { key: 'time', label: 'Time' },
        { key: 'kind', label: 'Type' },
        { key: 'eventName', label: 'What happened' },
        { key: 'step', label: 'Step' },
        { key: 'outcome', label: 'Result' },
        { key: 'timeSpent', label: 'Time spent' },
        { key: 'actions', label: '', thStyle: { width: '40px' } }
      ],
      detailOpen: false,
      selectedEvent: null,
      loadToken: 0,
      didAutoFocusClicks: false,
      liveAutoRefresh: false,
      liveRefreshTimer: null,
      quietRefresh: false
    };
  },
  watch: {
    sessionId: {
      immediate: true,
      handler() {
        this.stopLiveAutoRefresh();
        this.liveAutoRefresh = false;
        this.resetForSession();
        this.restoreFiltersForSession();
        this.loadDetail();
      }
    }
  },
  beforeDestroy() {
    this.stopLiveAutoRefresh();
  },
  computed: {
    fromSource() {
      return (this.$route && this.$route.query && this.$route.query.from) || 'live';
    },
    backLink() {
      const query = Object.assign({}, (this.$route && this.$route.query) || {});
      delete query.from;
      if (this.fromSource === 'sessions') return { path: '/tracking/sessions', query };
      if (this.fromSource === 'support') return { path: '/tracking/support', query };
      return { path: '/tracking/live-journeys', query };
    },
    backLabel() {
      if (this.fromSource === 'sessions') return 'Back to journeys';
      if (this.fromSource === 'support') return 'Back to support';
      return 'Back to live';
    },
    headerSubtitle() {
      if (this.session) {
        return `${this.prettyJourneyStatus(this.session)} · ${this.prettyStep(this.session.currentStep)}`;
      }
      return 'Loading timeline…';
    },
    failureCount() {
      return (this.events || []).filter((ev) => this.isFailedApi(ev)).length;
    },
    clickSummary() {
      const stage = this.selectedUiStage;
      const clicks = (this.events || []).filter((ev) => {
        const name = String(ev.eventName || '').toUpperCase();
        const isClick = name === 'FIELD_CLICKED' || name === 'CTA_CLICKED' || name === 'SLOT_SELECTED'
          || name === 'EXIT_REASON_SELECTED' || name === 'PAYMENT_METHOD_SELECTED';
        if (!isClick) return false;
        if (stage === 'WIDGET') return isWidgetEvent(ev);
        if (stage === 'CHECKOUT') return isRegisterEvent(ev) && CHECKOUT_MICRO_STEPS.includes(String(ev.step || '').toUpperCase());
        if (stage === 'CONFIRMATION' || stage === 'PROFILE' || stage === 'ADDITIONAL_DETAILS') {
          return String(ev.step || '').toUpperCase() === stage;
        }
        return true;
      });
      // Newest first — keep only the latest tap per field/button (count for Clicks only button).
      const sorted = clicks.slice().sort((a, b) => {
        const ta = new Date(a.occurredAt || a.createdAt || 0).getTime();
        const tb = new Date(b.occurredAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
      const seen = new Set();
      const latestOnly = [];
      sorted.forEach((ev) => {
        const key = this.clickDedupeKey(ev);
        if (seen.has(key)) return;
        seen.add(key);
        latestOnly.push(ev);
      });
      return latestOnly;
    },
    leadName() {
      if (!this.session) return '';
      const parts = [this.session.leadFirstName, this.session.leadLastName].filter(Boolean);
      return parts.join(' ');
    },
    uiStageOrder() {
      return patientUiStepOrder(
        this.session && this.session.userType,
        this.session && this.session.entrySurface,
        this.session && this.session.entryPage,
        this.events
      );
    },
    currentUiStage() {
      if (!this.session) return 'CHECKOUT';
      const step = this.session.currentStep;
      const page = this.session.currentPage || this.session.entryPage;
      return toPatientUiStage(step, { step, page });
    },
    progressSteps() {
      const order = this.uiStageOrder;
      const current = this.currentUiStage;
      const currentIdx = Math.max(0, order.indexOf(current));
      const completed = this.session && this.session.status === 'COMPLETED';
      const booked = !!(this.session && (this.session.orderId || this.session.appointmentId));
      const visits = this.stageVisitCounts;
      return order.map((code, index) => {
        const def = STEPS[code] || { short: code, label: code };
        let state = 'pending';
        if (completed || index < currentIdx) state = 'done';
        else if (index === currentIdx) state = 'current';
        // Confirmation is done once they booked, even if they later stall on profile/details.
        if (code === 'CONFIRMATION' && booked && state === 'pending') state = 'done';
        if ((code === 'WIDGET' || code === 'CHECKOUT') && booked && state === 'pending') state = 'done';
        const visitCount = visits[code] || 0;
        let hint = state === 'done'
          ? `${def.label} — completed`
          : state === 'current'
            ? `${def.label} — patient is here (click to filter timeline)`
            : `${def.label} — not reached yet (click to filter)`;
        if (visitCount > 1) {
          hint += ` · opened ${visitCount} times (includes refreshes)`;
        }
        return {
          code,
          index: index + 1,
          short: def.label || def.short || code,
          state,
          hint,
          visitCount
        };
      });
    },
    stageVisitCounts() {
      const counts = {};
      const events = this.events || [];
      const pageViews = events.filter((ev) => String(ev.eventName || '').toUpperCase() === 'PAGE_VIEWED');
      const source = pageViews.length
        ? pageViews
        : events.filter((ev) => String(ev.eventName || '').toUpperCase() === 'STEP_ENTERED');
      source.forEach((ev) => {
        const meta = ev.metadata || ev.metadataJson || {};
        const parseMeta = typeof meta === 'string'
          ? (() => { try { return JSON.parse(meta); } catch { return {}; } })()
          : meta;
        const stage = String(parseMeta.uiStage || parseMeta.UI_STAGE || toPatientUiStage(ev.step, ev) || '').toUpperCase();
        if (!stage) return;
        counts[stage] = (counts[stage] || 0) + 1;
      });
      return counts;
    },
    checkoutMicroSteps() {
      return CHECKOUT_MICRO_STEPS.map((code) => {
        const def = STEPS[code] || {};
        return {
          code,
          short: def.insideCheckout || code,
          label: def.insideCheckout || code
        };
      });
    },
    dropOffHint() {
      if (!this.session) return null;
      const status = this.session.status;
      const stage = this.currentUiStage;
      const stageLabel = (STEPS[stage] && STEPS[stage].label) || stage;
      const booked = !!(this.session.orderId || this.session.appointmentId);
      if (status === 'COMPLETED') {
        return {
          tone: 'ok',
          title: 'Finished every step',
          body: 'They completed checkout through additional details.'
        };
      }
      if (status === 'ABANDONED' || status === 'IDLE') {
        if (booked) {
          return {
            tone: 'warn',
            title: `Booked, then left at: ${stageLabel}`,
            body: 'Appointment is booked — they dropped off on a post-booking step. Click that step above to see what they did.'
          };
        }
        return {
          tone: 'bad',
          title: `Lost at: ${stageLabel}`,
          body: 'They left before finishing. Click that step above to inspect the timeline.'
        };
      }
      if (status === 'ACTIVE') {
        return {
          tone: 'info',
          title: `Currently on: ${stageLabel}`,
          body: 'Still active — click the step to filter live activity.'
        };
      }
      return null;
    },
    chatFilters() {
      return {
        sessionId: this.sessionId,
        step: this.filters.step || undefined,
        outcome: this.filters.outcome || undefined
      };
    },
    pageSummary() {
      if (!this.session) {
        return `Viewing session ${this.sessionId}. Timeline is loading.`;
      }
      const failedApis = (this.events || []).filter((ev) => this.isFailedApi(ev));
      const parts = [
        `Session ${this.sessionId}`,
        `patient ${this.leadName || 'unknown'}`,
        `email ${this.session.leadEmail || 'unknown'}`,
        `phone ${this.session.leadPhone || 'unknown'}`,
        `source ${this.formatSource(this.session)}`,
        `status ${this.prettyJourneyStatus(this.session)}`,
        `current step ${this.prettyStep(this.session.currentStep)}`,
        `${this.events.length} timeline events`
      ];
      if (failedApis.length) {
        const apiLines = failedApis.map((ev) => {
          const meta = ev.metadata || {};
          const method = String(meta.httpMethod || 'GET').toUpperCase();
          const path = ev.apiPath || 'unknown path';
          const status = ev.httpStatus != null ? ` HTTP ${ev.httpStatus}` : '';
          return `${method} ${path}${status}`;
        });
        parts.push(`${failedApis.length} failed API call(s): ${apiLines.join('; ')}`);
      } else {
        parts.push('no failed API calls recorded');
      }
      return parts.join('. ') + '.';
    },
    emptyFilterTitle() {
      if (this.selectedUiStage && this.selectedUiStage !== this.currentUiStage) {
        const label = (STEPS[this.selectedUiStage] && STEPS[this.selectedUiStage].label) || this.selectedUiStage;
        return `No activity on ${label} yet`;
      }
      return 'No events match these filters';
    },
    emptyFilterHint() {
      if (this.selectedUiStage === 'WIDGET' && this.filters.kind === 'ui') {
        return 'Widget often only has journey steps (Started / Moved). Switch Activity type to All activities, or open Checkout.';
      }
      if (this.selectedUiStage === 'WIDGET') {
        return 'No widget activity recorded for this journey, or they skipped the widget. Try Checkout.';
      }
      if (this.selectedUiStage === 'CONFIRMATION' || this.selectedUiStage === 'PROFILE' || this.selectedUiStage === 'ADDITIONAL_DETAILS') {
        return 'They had not reached this step. Click Widget or Checkout (or Clear) to see what they did.';
      }
      if (this.filters.kind === 'ui') {
        return 'Try Clear, or choose All activities.';
      }
      return 'Click Clear or choose Widget / Checkout to see their activity.';
    },
    /** Timeline rows with wait separators when gap between events is > 1s. */
    timelineDisplayRows() {
      const events = this.filteredEvents || [];
      const rows = [];
      events.forEach((item, index) => {
        const gapMs = item && item.gapMs != null ? Number(item.gapMs) : null;
        if (gapMs != null && gapMs > 1000) {
          rows.push({
            _rowType: 'gap',
            _gapKey: `gap-${index}-${gapMs}`,
            gapMs,
            // b-table needs a unique identity; avoid colliding with event ids
            clientEventId: `gap-${index}-${gapMs}`
          });
        }
        const enriched = Object.assign({}, item);
        enriched._repeatHint = this.fieldRepeatHint(item, events, index);
        rows.push(enriched);
      });
      return rows;
    }
  },
  methods: {
    prettyUserType,
    filterStorageKey() {
      return `trk-session-filters:${this.sessionId || ''}`;
    },
    restoreFiltersForSession() {
      if (!this.sessionId || typeof sessionStorage === 'undefined') return;
      try {
        const raw = sessionStorage.getItem(this.filterStorageKey());
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return;
        this.selectedUiStage = saved.selectedUiStage || '';
        this.filters.step = saved.step || '';
        this.filters.kind = saved.kind || '';
        this.filters.outcome = saved.outcome || '';
        // Skip auto-focus when we restored the admin's last tab.
        if (this.selectedUiStage || this.filters.kind || this.filters.step || this.filters.outcome) {
          this.didAutoFocusClicks = true;
        }
      } catch (_) {
        // ignore bad storage
      }
    },
    persistFiltersForSession() {
      if (!this.sessionId || typeof sessionStorage === 'undefined') return;
      try {
        sessionStorage.setItem(this.filterStorageKey(), JSON.stringify({
          selectedUiStage: this.selectedUiStage || '',
          step: this.filters.step || '',
          kind: this.filters.kind || '',
          outcome: this.filters.outcome || ''
        }));
      } catch (_) {
        // ignore quota
      }
    },
    resetForSession() {
      this.loadToken += 1;
      this.session = null;
      this.events = [];
      this.filteredEvents = [];
      this.loadError = '';
      this.detailOpen = false;
      this.selectedEvent = null;
      this.selectedUiStage = '';
      this.filters.step = '';
      this.filters.outcome = '';
      this.filters.kind = '';
      this.didAutoFocusClicks = false;
    },
    selectUiStage(code) {
      // Always keep one top stage selected — re-clicking the active chip does nothing.
      if (this.selectedUiStage === code) return;
      this.selectedUiStage = code;
      // Stage chips filter client-side only — never wipe the full event list.
      if (code === 'CHECKOUT') {
        this.filters.step = '';
      } else if (code === 'WIDGET') {
        this.filters.step = 'WIDGET';
      } else {
        this.filters.step = code;
      }
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    ensureUiStageSelected() {
      if (this.selectedUiStage) return;
      const current = this.currentUiStage || 'CHECKOUT';
      this.selectedUiStage = current;
      this.filters.step = current === 'CHECKOUT' ? '' : current;
    },
    filterCheckoutMicro(code) {
      this.selectedUiStage = 'CHECKOUT';
      this.filters.step = code;
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    clearCheckoutMicro() {
      this.selectedUiStage = 'CHECKOUT';
      this.filters.step = '';
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    sessionScopedEvents(events) {
      const sessionId = this.sessionId;
      return (events || []).filter((ev) => {
        if (!ev || !ev.sessionId) return true;
        return ev.sessionId === sessionId;
      });
    },
    loadDetail(options = {}) {
      const quiet = !!(options && options.quiet);
      const token = this.loadToken;
      // Always fetch the full timeline. Step/kind filters are applied client-side
      // so clicking Confirmation then back to Checkout never loses events.
      const params = {};
      this.quietRefresh = quiet;
      if (!quiet) {
        this.loading = true;
      }
      this.loadError = '';
      this.$onboardingTrackingService.getSession(this.sessionId, params).then((res) => {
        if (token !== this.loadToken) return;
        const data = (res && res.data) || res || {};
        this.session = data.session || null;
        this.events = this.sortEvents(this.sessionScopedEvents(data.events || []));
        if (!this.session) {
          this.loadError = 'Session not found.';
          this.filteredEvents = [];
          return;
        }
        if (!this.didAutoFocusClicks && !this.filters.kind && !this.filters.step && !this.filters.outcome && !this.selectedUiStage) {
          this.didAutoFocusClicks = true;
          const current = this.currentUiStage || 'CHECKOUT';
          this.selectedUiStage = current;
          this.filters.step = current === 'CHECKOUT' ? '' : current;
          this.persistFiltersForSession();
        } else {
          this.didAutoFocusClicks = true;
        }
        this.refreshFilteredEvents();
      }).catch(() => {
        if (token !== this.loadToken) return;
        if (!quiet) {
          this.session = null;
          this.events = [];
          this.filteredEvents = [];
          this.loadError = 'Unable to load this session. Check the ID or try again.';
        }
      }).finally(() => {
        if (token === this.loadToken) {
          this.loading = false;
          this.quietRefresh = false;
        }
      });
    },
    refreshLive() {
      this.loadDetail({ quiet: false });
    },
    onLiveAutoRefreshChange() {
      if (this.liveAutoRefresh) {
        this.startLiveAutoRefresh();
        this.loadDetail({ quiet: true });
      } else {
        this.stopLiveAutoRefresh();
      }
    },
    startLiveAutoRefresh() {
      this.stopLiveAutoRefresh();
      this.liveRefreshTimer = setInterval(() => {
        if (!this.sessionId || this.loading) {
          return;
        }
        this.loadDetail({ quiet: true });
      }, 10000);
    },
    stopLiveAutoRefresh() {
      if (this.liveRefreshTimer) {
        clearInterval(this.liveRefreshTimer);
        this.liveRefreshTimer = null;
      }
    },
    eventsMatchingStage(events) {
      const list = events || [];
      const stage = this.selectedUiStage;
      const step = String(this.filters.step || '').toUpperCase();
      if (stage === 'WIDGET' || step === 'WIDGET') {
        return list.filter((ev) => isWidgetEvent(ev));
      }
      if (stage === 'CHECKOUT') {
        if (step && CHECKOUT_MICRO_STEPS.includes(step)) {
          return list.filter((ev) =>
            String(ev.step || '').toUpperCase() === step && isRegisterEvent(ev)
          );
        }
        return list.filter((ev) =>
          CHECKOUT_MICRO_STEPS.includes(String(ev.step || '').toUpperCase()) && isRegisterEvent(ev)
        );
      }
      if (stage === 'CONFIRMATION' || step === 'CONFIRMATION') {
        return list.filter((ev) => String(ev.step || '').toUpperCase() === 'CONFIRMATION');
      }
      if (stage === 'PROFILE' || step === 'PROFILE') {
        return list.filter((ev) => String(ev.step || '').toUpperCase() === 'PROFILE');
      }
      if (stage === 'ADDITIONAL_DETAILS' || step === 'ADDITIONAL_DETAILS') {
        return list.filter((ev) => String(ev.step || '').toUpperCase() === 'ADDITIONAL_DETAILS');
      }
      if (step) {
        if (CHECKOUT_MICRO_STEPS.includes(step)) {
          return list.filter((ev) =>
            String(ev.step || '').toUpperCase() === step && !isWidgetEvent(ev)
          );
        }
        return list.filter((ev) => String(ev.step || '').toUpperCase() === step);
      }
      return list.slice();
    },
    applyClientFilters(events) {
      const kind = this.filters.kind;
      let list = this.eventsMatchingStage(events);
      if (kind) {
        list = list.filter((ev) => describeActivity(ev.eventName, ev).kind === kind);
      }
      const outcome = String(this.filters.outcome || '').toUpperCase();
      if (outcome === 'FAILURE') {
        list = list.filter((ev) => this.isFailedApi(ev));
      } else if (outcome) {
        list = list.filter((ev) => String((ev && ev.outcome) || '').toUpperCase() === outcome);
      }
      return list;
    },
    refreshFilteredEvents() {
      this.filteredEvents = this.sortEvents(this.applyClientFilters(this.events));
    },
    applyFilters() {
      // Keep stage chip in sync with the step dropdown when possible.
      const step = String(this.filters.step || '').toUpperCase();
      if (!step) {
        // leave selectedUiStage as-is unless clearing entirely via clearFilters
      } else if (CHECKOUT_MICRO_STEPS.includes(step)) {
        this.selectedUiStage = 'CHECKOUT';
      } else if (step === 'WIDGET' || step === 'CONFIRMATION' || step === 'PROFILE' || step === 'ADDITIONAL_DETAILS') {
        this.selectedUiStage = step;
      }
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    clearFilters() {
      this.filters.outcome = '';
      this.filters.kind = '';
      this.ensureUiStageSelected();
      if (this.selectedUiStage === 'CHECKOUT') {
        this.filters.step = '';
      } else {
        this.filters.step = this.selectedUiStage;
      }
      this.persistFiltersForSession();
      this.loadDetail();
    },
    showFailuresOnly() {
      this.filters.outcome = 'FAILURE';
      this.filters.kind = '';
      this.ensureUiStageSelected();
      if (this.selectedUiStage === 'CHECKOUT') {
        this.filters.step = '';
      } else {
        this.filters.step = this.selectedUiStage;
      }
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    showPatientClicksOnly() {
      this.filters.kind = 'ui';
      this.filters.outcome = '';
      this.ensureUiStageSelected();
      // Keep Widget vs Checkout separation when already selected.
      if (this.selectedUiStage === 'WIDGET') {
        this.filters.step = 'WIDGET';
      } else if (this.selectedUiStage === 'CHECKOUT') {
        this.filters.step = '';
      } else {
        this.filters.step = this.selectedUiStage;
      }
      this.persistFiltersForSession();
      this.refreshFilteredEvents();
    },
    clickLabel(item) {
      return describeActivity(item.eventName, item).label || this.prettyEvent(item.eventName, item);
    },
    clickDedupeKey(item) {
      const name = String(item && item.eventName || '').toUpperCase();
      const meta = item && (item.metadata || item.metadataJson) || {};
      const parseMeta = typeof meta === 'string'
        ? (() => { try { return JSON.parse(meta); } catch { return {}; } })()
        : meta;
      const field = String(
        parseMeta.fieldName || parseMeta.FIELD_NAME || parseMeta.fieldLabel || parseMeta.FIELD_LABEL || ''
      ).trim().toLowerCase();
      const action = String(parseMeta.action || parseMeta.ACTION || '').trim().toLowerCase();
      const step = String(item && item.step || '').toUpperCase();
      if (name === 'FIELD_CLICKED') return `field:${step}:${field || this.clickLabel(item)}`;
      if (name === 'CTA_CLICKED') return `cta:${step}:${action || this.clickLabel(item)}`;
      // One latest slot pick only — different slotIds were showing as 4× "Selected an appointment time".
      if (name === 'SLOT_SELECTED') return `slot:${step}`;
      return `${name}:${step}:${field || action || this.clickLabel(item)}`;
    },
    eventStepLabel(item) {
      if (isWidgetEvent(item)) return 'Widget';
      if (isRegisterEvent(item)) {
        const stage = toPatientUiStage(item.step, item);
        if (stage === 'CHECKOUT') return 'Register · Checkout';
        return `Register · ${this.prettyStep(item && item.step)}`;
      }
      return this.prettyStep(item && item.step);
    },
    describeActivity,
    kindTone(item) {
      const kind = describeActivity(item.eventName, item).kind;
      if (kind === 'api' && this.isFailure(item)) return 'bad';
      if (kind === 'api') return 'info';
      if (kind === 'ui') return 'warn';
      return 'ok';
    },
    toggleOrder() {
      this.newestFirst = !this.newestFirst;
      this.events = this.sortEvents(this.events);
      this.filteredEvents = this.sortEvents(this.filteredEvents);
    },
    copySessionId() {
      if (!this.sessionId || !navigator.clipboard) return;
      navigator.clipboard.writeText(this.sessionId).catch(() => {});
    },
    formatSource(row) {
      return formatReferrer(row || {});
    },
    sourceHint,
    downloadJourney() {
      if (!this.sessionId) return;
      this.$onboardingTrackingService.exportJourney(this.sessionId);
    },
    showFailedApiPayload(item) {
      return this.isFailedApi(item) && !!this.eventApiCurl(item);
    },
    isFailedApi(item) {
      if (!item || !this.isFailure(item)) {
        return false;
      }
      const name = String(item.eventName || '').toUpperCase();
      return name === 'API_CALL' || !!item.apiPath;
    },
    eventApiCurl(item) {
      return buildEventApiCurl(item, PATIENT_API_BASE);
    },
    responseBodyOf(item) {
      const meta = (item && item.metadata) || {};
      const raw = meta.responseBody;
      if (!raw) {
        return '';
      }
      if (typeof raw === 'string') {
        try {
          return JSON.stringify(JSON.parse(raw), null, 2);
        } catch (e) {
          return raw;
        }
      }
      try {
        return JSON.stringify(raw, null, 2);
      } catch (e) {
        return String(raw);
      }
    },
    sortEvents(events) {
      return (events || []).slice().sort((a, b) => {
        const ta = new Date(a.occurredAt || a.createdAt || 0).getTime();
        const tb = new Date(b.occurredAt || b.createdAt || 0).getTime();
        return this.newestFirst ? (tb - ta) : (ta - tb);
      });
    },
    openEventDetail(item) {
      this.selectedEvent = item;
      this.detailOpen = true;
    },
    isFailure(item) {
      if (isApiActivity(item && item.eventName, item)) {
        return isTrackedApiFault(item);
      }
      return String((item && item.outcome) || '').toUpperCase() === 'FAILURE';
    },
    hasErrorFields(item) {
      if (this.isFailedApi(item)) {
        return true;
      }
      if (isApiActivity(item && item.eventName, item)) {
        return false;
      }
      return !!(item && (item.errorCode || this.errorMessageOf(item)));
    },
    metaValue(item, key) {
      const meta = (item && item.metadata) || {};
      return meta[key];
    },
    errorMessageOf(item) {
      const meta = (item && item.metadata) || {};
      return meta.errorMessage || meta.message || null;
    },
    metadataEntries(item) {
      const meta = (item && item.metadata) || {};
      return Object.keys(meta)
        .filter((key) => meta[key] !== undefined && meta[key] !== null && meta[key] !== '')
        .map((key) => ({ key, value: String(meta[key]) }));
    },
    prettyMetaKey(key) {
      return String(key)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase());
    },
    prettyEvent(name, item) {
      if (!name) return '-';
      return describeActivity(name, item || {}).label;
    },
    prettyStep(step) {
      return trackingLabels.prettyStep(step);
    },
    prettyStatus(status) {
      return trackingLabels.prettyStatus(status);
    },
    prettyJourneyStatus(row) {
      return trackingLabels.prettyJourneyStatus(row || this.session);
    },
    journeyStatusTitle(row) {
      return trackingLabels.journeyStatusTitle(row || this.session);
    },
    prettyEntrySurface(surface) {
      return trackingLabels.prettyEntrySurface(surface);
    },
    outcomeLabel(itemOrStatus) {
      if (itemOrStatus && typeof itemOrStatus === 'object') {
        if (isApiActivity(itemOrStatus.eventName, itemOrStatus)) {
          if (isServerErrorStatus(itemOrStatus.httpStatus)) {
            return 'HTTP ' + itemOrStatus.httpStatus;
          }
          if (itemOrStatus.httpStatus != null) {
            return 'HTTP ' + itemOrStatus.httpStatus;
          }
          return 'API';
        }
        itemOrStatus = itemOrStatus.outcome;
      }
      if (!itemOrStatus) return '-';
      const upper = String(itemOrStatus).toUpperCase();
      if (upper === 'SUCCESS') return 'Worked';
      if (upper === 'FAILURE') return 'Failed';
      if (upper === 'INFO') return 'Info';
      return trackingLabels.prettyStatus(itemOrStatus);
    },
    statusTone(itemOrStatus) {
      if (itemOrStatus && typeof itemOrStatus === 'object') {
        if (isApiActivity(itemOrStatus.eventName, itemOrStatus)) {
          return isTrackedApiFault(itemOrStatus) ? 'bad' : 'info';
        }
        itemOrStatus = itemOrStatus.outcome;
      }
      const upper = String(itemOrStatus || '').toUpperCase();
      if (upper === 'ABANDONED' || upper === 'FAILURE') return 'bad';
      if (upper === 'IDLE') return 'warn';
      if (upper === 'COMPLETED' || upper === 'SUCCESS') return 'ok';
      return 'info';
    },
    capitalize(value) {
      if (!value) return '-';
      return String(value).charAt(0).toUpperCase() + String(value).slice(1);
    },
    formatDateTime(value) {
      return formatUkDateTime(value, { withSeconds: false });
    },
    formatTime(value) {
      return formatUkTime(value);
    },
    formatDurationMs(ms) {
      const s = Math.max(0, Math.round((ms || 0) / 1000));
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m + 'm ' + String(r).padStart(2, '0') + 's';
    },
    formatShortMs(ms) {
      if (ms == null) return '—';
      if (ms < 1000) return ms + 'ms';
      if (ms < 60000) return (ms / 1000).toFixed(ms < 10000 ? 1 : 0) + 's';
      const s = Math.round(ms / 1000);
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m + 'm ' + String(r).padStart(2, '0') + 's';
    },
    actionDurationMs(item) {
      if (!item) return null;
      if (item.durationMs != null) return item.durationMs;
      const meta = item.metadata || {};
      return meta.durationMs != null ? Number(meta.durationMs) : null;
    },
    formatTimeSpent(item) {
      if (!item) return '—';
      const preferred = item.stepDwellMs != null ? item.stepDwellMs : item.gapMs;
      if (preferred == null) return '—';
      const ms = Number(preferred);
      if (ms > 1000) {
        return 'waited ' + this.formatShortMs(ms);
      }
      return this.formatShortMs(ms);
    },
    waitGapLabel(gapRow) {
      const ms = gapRow && gapRow.gapMs != null ? Number(gapRow.gapMs) : 0;
      return 'Waited ' + this.formatShortMs(ms) + ' between actions';
    },
    timelineRowClass(item) {
      if (item && item._rowType === 'gap') {
        return 'trk-timeline-gap-row';
      }
      const classes = [];
      if (this.isFailedApi(item)) {
        classes.push('trk-timeline-500-row');
      }
      if (item && Number(item.gapMs) > 1000) {
        classes.push('trk-timeline-after-wait');
      }
      return classes.join(' ');
    },
    onTimelineRowClicked(item) {
      if (!item || item._rowType === 'gap') {
        return;
      }
      this.openEventDetail(item);
    },
    fieldRepeatHint(item, events, index) {
      if (!item || String(item.eventName || '').toUpperCase() !== 'FIELD_CLICKED') {
        return '';
      }
      // Chronological previous event: with newest-first, that is the next row in the list.
      const prev = this.newestFirst ? events[index + 1] : events[index - 1];
      if (!prev || String(prev.eventName || '').toUpperCase() !== 'FIELD_CLICKED') {
        return '';
      }
      const a = this.clickDedupeKey(item);
      const b = this.clickDedupeKey(prev);
      if (!a || a !== b) {
        return '';
      }
      const gapMs = item.gapMs != null ? Number(item.gapMs) : 0;
      if (gapMs > 1000) {
        return 'Same field again after ' + this.formatShortMs(gapMs);
      }
      return 'Same field again';
    },
    relativeTime(value) {
      if (!value) return '-';
      const sec = Math.floor(Math.max(0, Date.now() - new Date(value).getTime()) / 1000);
      if (sec < 60) return sec + ' sec ago';
      return Math.floor(sec / 60) + ' min ago';
    }
  }
};
</script>

<style scoped>
.trk-live-auto {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  font-size: 0.8rem;
  color: #475569;
  cursor: pointer;
  user-select: none;
}
.trk-live-auto input {
  margin: 0;
}
.trk-table-refresh-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.trk-table-refresh-hint {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.trk-progress-ribbon {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: stretch;
}
.trk-wait-gap-time {
  color: #c2410c;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.trk-wait-gap {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  min-width: 220px;
  padding: 0.15rem 0;
}
.trk-wait-gap-line {
  flex: 1;
  height: 0;
  border-top: 1px dashed #cbd5e1;
}
.trk-wait-gap-chip {
  flex: 0 0 auto;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.trk-field-repeat-hint {
  margin-top: 0.2rem;
  color: #b45309;
  font-size: 0.75rem;
}
.time-spent-wait {
  color: #9a3412;
  font-weight: 600;
}
::v-deep .timeline-table tr.trk-timeline-500-row td {
  background: #fde8e8;
}
::v-deep .timeline-table tr.trk-timeline-gap-row td {
  background: #fffaf5;
  border-top: none;
  border-bottom: none;
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
  vertical-align: middle;
}
::v-deep .timeline-table tr.trk-timeline-gap-row:hover td {
  background: #fff7ed;
}
::v-deep .timeline-table tr.trk-timeline-after-wait td {
  border-top-color: transparent;
}
.trk-progress-step {
  position: relative;
  flex: 1 1 140px;
  min-width: 120px;
  text-align: center;
  padding: 0.55rem 0.45rem;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
}
.trk-visit-badge {
  position: absolute;
  top: -0.35rem;
  right: -0.35rem;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #0e7490;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.25rem;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
  z-index: 1;
}
.trk-progress-step.done {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}
.trk-progress-step.current {
  background: #ecfeff;
  border-color: #67e8f9;
  color: #0e7490;
  font-weight: 600;
}
.trk-progress-step.selected {
  box-shadow: 0 0 0 2px #0e7490;
}
.trk-progress-dot {
  width: 1.6rem;
  height: 1.6rem;
  margin: 0 auto 0.35rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  background: #fff;
  border: 1px solid currentColor;
}
.trk-progress-label {
  font-size: 0.78rem;
  line-height: 1.2;
}
.trk-lost-callout {
  border-radius: 10px;
  padding: 0.85rem 1rem;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}
.trk-lost-callout.ok {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.trk-lost-callout.warn {
  background: #fffbeb;
  border-color: #fde68a;
}
.trk-lost-callout.bad {
  background: #fef2f2;
  border-color: #fecaca;
}
.trk-lost-callout.info {
  background: #ecfeff;
  border-color: #a5f3fc;
}
.trk-micro-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.trk-micro-chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.78rem;
  color: #475569;
  cursor: pointer;
}
.trk-micro-chip.active {
  background: #0e7490;
  border-color: #0e7490;
  color: #fff;
}
</style>
